import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import enums from 'scoreboard/components/enums'
import constants from 'scoreboard/components/constants'
import { getPlotTypeLabelConf } from './label-settings'
import GeographicMapLevel from 'scoreboard/charts/model/geographic-map-level'
const {
	EARTH3D,
	EARTH2D,
	CONTINENT,
	POSTAL_CODE,
	COUNTY,
	CITY
} = GeographicMapLevel

function getPlotTypeMsgCode(plotType) {
	switch (plotType) {
		case undefined:
			return ''
		case 'pie':
			return 'charts.chart.plotType.pie.slice'
		case 'donut':
			return 'charts.chart.plotType.donut.slice'
		default:
			return enums['plotType'][plotType]
	}
}

function getAxesSettingsLabel(model) {
	// add axes setting label - all/x axis/y axis/no labels
	const xAxisLabels = model.get('xaxisLabels')
	const yAxisLabels = model.get('yaxisLabels')
	let beginningMessageCode = 'common.no'
	if (xAxisLabels && yAxisLabels) {
		beginningMessageCode = 'common.all'
	} else if (xAxisLabels || yAxisLabels) {
		beginningMessageCode = xAxisLabels ?
			'charts.chart.xAxis' : 'charts.chart.yAxis'
	}
	let axesMessage = i18n.get('datasets.filterOnValues',
		beginningMessageCode, 'common.labels') + ', '

	// add range label - auto/manual range
	let rangeAttributes = _.pick(model.attributes,'rightAxisMin', 'rightAxisMax',
		'leftAxisMin', 'leftAxisMax')
	let rangeTypeCode = _.some(rangeAttributes, attr => !_.isNil(attr)) ?
		'scorecards.node.metric.updateType.manual' : 'common.auto'
	axesMessage += i18n.get('datasets.filterOnValues',
		rangeTypeCode, 'common.range')

	return axesMessage
}

/**
 * Helper functions and constants for working with chart settings
 */
const settingsHelper = {
	/**
	 * returns the allowed map levels given the current geographic field's
	 * data type
	 */
	getValidMapLevels: function({ dataType, treeId }) {
		const mapLevels = _.keys(enums.geographicMapLevel)
		if (!_.includes(mapLevels, dataType)) {
			return mapLevels
		}

		let typeIndex = mapLevels.indexOf(dataType)
		let validTypes = mapLevels.slice(0, typeIndex + 1)
		if (dataType === POSTAL_CODE) {
			_.remove(validTypes, type => type === COUNTY)
		}
		if (dataType === CITY) {
			_.remove(validTypes, type => type === POSTAL_CODE)
		}
		if (treeId === constants.POSTAL_CODES_ROLLUP_ID) {
			validTypes = _.filter(validTypes, type =>
				type !== CONTINENT && type !== EARTH2D && type !== EARTH3D)
		}
		return validTypes
	},

	/**
	 * Generate the label for an "other settings" setting that is specific to
	 * a plot type
	 *
	 * @param {String || undefined} plotType
	 * @param {String} settingType
	 */
	getSettingPlotTypeLabel: function({ plotType, settingType }) {
		const settingTypeCode = `charts.chart.settingType.${settingType}`

		// #41360
		let plotTypeSettingWithArgs = 'charts.chart.plotTypeSetting'
		let plotTypeCode = `charts.chart.plotType.${_.camelCase(plotType)}`
		let plotTypeSliceCode = `charts.chart.plotType.${plotType}.slice`

		switch (settingType) {
			case 'totalContainerValue':
				return i18n.get('charts.chart.totalContainerValue')
			case 'pieLabels':
				// plotType could be pie or donut
				return i18n.get(`charts.chart.${plotType}Labels`)
			case 'colors':
				if ([ 'line', 'area' ].includes(plotType)) {
					// #40220 use custom text for the 'score' option for lines #40306 and areas
					return i18n.get(`charts.chart.plotTypeSetting.${plotType}`)
				}
				if (['pie','donut'].includes(plotType)) {
					// #41614 #41360 use 'slice' for pie and donut
					return i18n.get(plotTypeSettingWithArgs, plotTypeSliceCode, settingTypeCode)
				}
				// #41360 all others use 'charts.chart.plotTypeSetting' sending args
				// for plot type and setting type
				return i18n.get(plotTypeSettingWithArgs, plotTypeCode, settingTypeCode)
			case 'labels':
				if (['pie','donut'].includes(plotType)) {
					// use 'slice'
					return i18n.get(plotTypeSettingWithArgs, plotTypeSliceCode, settingTypeCode)
				}
				if (['process', 'tree-map', 'cycle', 'timeline', 'funnel',
					'pyramid', 'waffle', 'filled-container', 'bar',
					'line', 'area'].includes(plotType)) {
					return i18n.get(plotTypeSettingWithArgs, plotTypeCode, settingTypeCode)
				}
				break
			case 'legends':
				return i18n.get('charts.chart.settingType.colorLegend')
		}
		return i18n.get(settingTypeCode)
	},

	/**
	 * Generate the secondary text under the primary label for an "other settings"
	 * setting
	 */
	getSettingSubText: function(setting, model) {
		switch (setting.settingType) {
			case 'colors':
				if (setting.colorType === 'score' && [ 'line', 'area' ].includes(setting.plotType)) {
					return i18n.get(`charts.chart.colorType.score.${setting.plotType}`)
				}
				return i18n.get(`charts.chart.colorType.${setting.colorType}`)
			case 'labels':
				const enabledList = []
				if (setting.seriesLabels) {
					const conf = getPlotTypeLabelConf(setting.plotType)
					enabledList.push(i18n.get(`charts.series.labels.${conf.chartedValuesCount}`))
				}
				if (setting.stageNumbers) {
					enabledList.push(i18n.get('charts.chart.labels.stageNumbers'))
				}
				if (setting.percentsRemaining) {
					enabledList.push(i18n.get('charts.chart.labels.percentsRemaining'))
				}
				if (setting.percentages) {
					enabledList.push(i18n.get('charts.chart.labels.percentages'))
				}
				if (setting.chartedValues) {
					enabledList.push(i18n.get('charts.chart.labels.chartedValues'))
				}
				if (setting.descriptionType === 'scorecard') {
					enabledList.push(i18n.get('charts.chart.scorecardItemDescription'))
				}
				if (setting.descriptionType === 'initiative') {
					enabledList.push(i18n.get('charts.chart.labels.initiativeDescription'))
				}
				if (setting.descriptionType === 'custom') {
					enabledList.push(i18n.get('charts.chart.labels.customDescription'))
				}
				return _.isEmpty(enabledList) ? i18n.get('common.none') :
					_.join(enabledList, ', ')
			case 'legends':
				return i18n.get(`charts.chart.legend.${setting.legendType}`)
			case 'axes':
				return getAxesSettingsLabel(model)
			case 'totalContainerValue':
				return model && model.get('totalContainerValue')
			case 'pieLabels':
				const pieLabelsEnabled = model && model.get('pieLabels')
				return i18n.get(pieLabelsEnabled ? 'charts.chart.pieLabel' :
					'common.none')
			case 'colorLegend':
			case 'bubbleLegend':
			case 'spikeLegend':
				const legendType = model.get(`${setting.settingType}Type`)
				return i18n.get(`charts.chart.legend.${legendType}`)
			case 'chartTable':
				return model && model.get('chartTable') ?
					i18n.get('charts.chart.settingType.chartTable.show.true') :
						i18n.get('charts.chart.settingType.chartTable.show.false')
			case 'visibleHierarchyLevels':
				return model && model.get('rollupLevels')
			case 'equallySizedStages':
				return i18n.get(model && model.get('equallySizedStages') ?
					'common.on' : 'common.off')
			case 'startingView':
				let subText = i18n.get('common.auto')
				const startingViewTreeId = model.get('startingViewTreeId')
				if (!_.isNil(startingViewTreeId)) {
					const treeItems = model.data?.treeItems() || {}
					const label = _.get(treeItems[startingViewTreeId], 'label')
					subText = label || i18n.get(
						'charts.chart.settingType.startingView.specificRegion')
				}
				return subText
			case 'mapLevels':
				const titles = _(enums.toOptionData('geographicMapLevel'))
					.filter(type => model.get(`${_.camelCase(type.id)}Enabled`))
					.map(type => type.label)
					.value()
				return _.join(titles, ', ')
			case 'bubbleSize':
			case 'spikeHeight':
				const messageCode =
					_.isNil(model.get(`${setting.settingType}Min`)) &&
					_.isNil(model.get(`${setting.settingType}Max`)) ?
					'common.auto' : 'scorecards.node.metric.updateType.manual'
				return i18n.get(messageCode)
			default:
				return ''
		}
	},

	getPlotTypeMsgCode
}

export default settingsHelper
settingsHelper.__filename__ = 'charts/edit/settings-helper.js'
